import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary, useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';

interface FeatureFlag {
  _id: string;
  key: string;
  description: string;
  rollout: 'off' | 'per-tenant' | 'all';
  perTenantOverrides: Array<{ tenant: string; tenantName: string | null; enabled: boolean }>;
  updatedBy?: string;
  updatedAt: string;
}

const DEFAULT_FLAGS = [
  { key: 'assessments_module', description: 'Question bank + Exams' },
  { key: 'nexie_telugu_beta', description: 'Chat and briefs in Telugu' },
  { key: 'whatsapp_own_number', description: "Sender via tenant's own MSG91 line" },
  { key: 'adaptive_exams', description: 'Question difficulty adjusts per candidate' },
  { key: 'alumni_network', description: 'Alumni-side app + directory' },
  { key: 'sso_google', description: 'Institution-wide Google login' },
];

/**
 * /platform/flags — live feature-flag matrix.
 * Rollout has three states: off / per-tenant / all. Per-tenant mode reveals a list of
 * enabled tenants and a shortcut to open the tenant-detail Features tab.
 */
export function PlatformFlagsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['platform', 'flags'],
    queryFn: () => apiGet<{ flags: FeatureFlag[] }>('/platform/flags'),
  });

  const setRollout = useMutation({
    mutationFn: (input: { key: string; description: string; rollout: 'off' | 'per-tenant' | 'all' }) =>
      apiPost('/platform/flags', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platform', 'flags'] }),
  });

  const existing = new Map((query.data?.flags ?? []).map((f) => [f.key, f]));
  const rows = DEFAULT_FLAGS.map((d) => existing.get(d.key) ?? ({
    _id: 'unset:' + d.key,
    key: d.key,
    description: d.description,
    rollout: 'off',
    perTenantOverrides: [],
    updatedAt: '',
  } as FeatureFlag));

  const rolloutTone = (r: string) => r === 'all' ? 'badge-success' : r === 'per-tenant' ? 'badge-warning' : '';

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Feature flags &amp; settings</h1>
          <p className="subhead">Flip capabilities per tenant. Every change writes to the audit log.</p>
        </div>
      </div>

      <div className="card">
        <AsyncBoundary kind="table" inCard loading={query.isLoading} error={query.error} empty={rows.length === 0} emptyTitle="No flags configured">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Flag</th>
                  <th className="hide-mobile">Description</th>
                  <th>Rollout</th>
                  <th className="hide-mobile">Enabled tenants</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.key}>
                    <td className="cell-title">{f.key}</td>
                    <td className="hide-mobile cell-sub">{f.description}</td>
                    <td>
                      <div className="segmented">
                        {(['off', 'per-tenant', 'all'] as const).map((r) => (
                          <button
                            key={r}
                            aria-pressed={f.rollout === r}
                            onClick={() => setRollout.mutate(
                              { key: f.key, description: f.description, rollout: r },
                              {
                                onSuccess: () => toast.show({ message: `${f.key} set to ${r}.` }),
                                onError: (e) => toast.show({ tone: 'warning', message: (e as Error)?.message ?? 'Failed' }),
                              },
                            )}
                          >
                            {r === 'per-tenant' ? 'Per tenant' : r === 'all' ? 'All' : 'Off'}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="hide-mobile">
                      {f.rollout === 'per-tenant' ? (
                        <span className={`badge ${rolloutTone(f.rollout)}`}>{f.perTenantOverrides.filter((o) => o.enabled).length} tenants</span>
                      ) : f.rollout === 'all' ? (
                        <span className="badge badge-success">Everyone</span>
                      ) : (
                        <span className="cell-sub">—</span>
                      )}
                    </td>
                    <td className="r cell-sub">
                      {f.updatedAt ? new Date(f.updatedAt).toLocaleString('en-IN') : 'Not saved'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <p className="helper">
        Per-tenant overrides are edited on each tenant's detail page (Features tab). Changing rollout globally does not clear existing overrides.
      </p>
    </div>
  );
}
